/**
 * Redirect URL Validation Utilities
 *
 * Prevents open redirect attacks by validating redirect URLs.
 * Only allows relative paths or paths matching our known apps.
 *
 * @module router/redirectUtils
 */

/**
 * Patterns for allowed redirect URLs.
 * Add new app patterns here as satellite apps are created.
 */
const ALLOWED_REDIRECT_PATTERNS = [
  /^\/(?!\/)/, // Relative paths (starts with single /, not //)
  /^\/bbweb\//, // bbcedit paths
  /^\/text-indexer\//, // text-indexer paths (if not under bbweb)
];

/**
 * Validate redirect URL to prevent open redirect attacks.
 * Only allows relative paths or paths matching our apps.
 *
 * @param {string} url - URL to validate
 * @param {string} [fallback='/home'] - Fallback URL if validation fails
 * @returns {string} Validated URL or fallback
 *
 * @example
 * ```js
 * // Safe - returns the URL
 * validateRedirectUrl('/bbweb/contracts'); // '/bbweb/contracts'
 * validateRedirectUrl('/home'); // '/home'
 *
 * // Blocked - returns fallback
 * validateRedirectUrl('https://evil.com'); // '/home'
 * validateRedirectUrl('//evil.com'); // '/home'
 * validateRedirectUrl('javascript:alert(1)'); // '/home'
 * ```
 */
export function validateRedirectUrl(url, fallback = '/home') {
  if (!url) return fallback;

  // Decode if URL-encoded
  let decoded;
  try {
    decoded = decodeURIComponent(url);
  } catch {
    console.warn('[Auth] Failed to decode redirect URL:', url);
    return fallback;
  }

  // Trim whitespace
  decoded = decoded.trim();

  // Block empty strings
  if (!decoded) return fallback;

  // Block javascript: protocol
  if (decoded.toLowerCase().startsWith('javascript:')) {
    console.warn('[Auth] Blocked javascript: redirect:', url);
    return fallback;
  }

  // Block data: protocol
  if (decoded.toLowerCase().startsWith('data:')) {
    console.warn('[Auth] Blocked data: redirect:', url);
    return fallback;
  }

  // Check against allowed patterns
  if (ALLOWED_REDIRECT_PATTERNS.some((pattern) => pattern.test(decoded))) {
    return decoded;
  }

  console.warn('[Auth] Blocked potentially malicious redirect:', url);
  return fallback;
}

/**
 * Parse redirect from search params safely.
 *
 * @param {Record<string, string>} search - Search params object
 * @param {string} [fallback='/home'] - Fallback URL if no valid redirect found
 * @returns {string} Validated redirect URL
 *
 * @example
 * ```js
 * // From TanStack Router useSearch()
 * const search = useSearch({ strict: false });
 * const redirectTo = getRedirectFromSearch(search);
 * navigate({ to: redirectTo });
 * ```
 */
export function getRedirectFromSearch(search, fallback = '/home') {
  if (!search || typeof search !== 'object') {
    return fallback;
  }

  // Check common redirect param names
  const redirectUrl = search.redirect || search.returnTo || search.next || '';

  return validateRedirectUrl(redirectUrl, fallback);
}

/**
 * Build a login URL with a safe redirect parameter.
 *
 * @param {string} loginPath - Base login path (e.g., '/bbweb/login')
 * @param {string} [redirectTo] - URL to redirect to after login
 * @returns {string} Login URL with redirect param
 *
 * @example
 * ```js
 * buildLoginUrl('/bbweb/login', '/bbweb/contracts');
 * // Returns: '/bbweb/login?redirect=/bbweb/contracts'
 * ```
 */
export function buildLoginUrl(loginPath, redirectTo) {
  if (!redirectTo) {
    return loginPath;
  }

  const safeRedirect = validateRedirectUrl(redirectTo, '');
  if (!safeRedirect) {
    return loginPath;
  }

  const separator = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${separator}redirect=${encodeURIComponent(safeRedirect)}`;
}
